/**
 * Card perks: period maths and API types.
 *
 * A TypeScript port of the iOS PerksKit package (ios/Packages/PerksKit), so
 * both clients agree on which quarter a credit is in and what's left of it.
 * Dates are local calendar days; API dates are plain `yyyy-MM-dd`.
 */

export type Cadence = 'monthly' | 'quarterly' | 'semiannual' | 'annual' | 'every_four_years'
export type ResetBasis = 'calendar' | 'cardmember_year'
export type BenefitKind = 'credit' | 'certificate'

export const CADENCES: Cadence[] = ['monthly', 'quarterly', 'semiannual', 'annual', 'every_four_years']

export const CADENCE_MONTHS: Record<Cadence, number> = {
  monthly: 1,
  quarterly: 3,
  semiannual: 6,
  annual: 12,
  every_four_years: 48,
}

export const CADENCE_LABEL: Record<Cadence, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semiannual: 'Every 6 months',
  annual: 'Yearly',
  every_four_years: 'Every 4 years',
}

const CADENCE_SHORT: Record<Cadence, string> = {
  monthly: '/mo',
  quarterly: '/qtr',
  semiannual: '/half',
  annual: '/yr',
  every_four_years: '/4 yrs',
}

// MARK: API types (GET /api/perks)

export interface PerkUsage {
  id: number
  date: string
  amount_cents: number
  is_skip: boolean
  note: string
  source: 'manual' | 'bank'
  transaction_id: string | null
  transaction_name: string | null
}

export interface PerkBenefit {
  id: number
  spec_id: string | null
  name: string
  detail: string
  amount_cents: number
  cadence: Cadence
  basis: ResetBasis
  kind: BenefitKind
  requires_enrollment: boolean
  is_enrolled: boolean
  reminders_on: boolean
  month_overrides: Record<string, number>
  auto_detect: boolean
  sort_index: number
  usages: PerkUsage[]
}

export interface PerkCard {
  id: number
  account_id: string | null
  mask: string | null
  template_id: string | null
  name: string
  issuer: string
  opened_on: string
  annual_fee_cents: number
  art: string
  sort_index: number
  benefits: PerkBenefit[]
}

export interface PerkUnmatchedCredit {
  transaction_id: string
  card_id: number
  date: string
  amount_cents: number
  name: string
}

export interface PerkPurchaseSignal {
  benefit_id: number
  transaction_id: string
  date: string
  amount_cents: number
  name: string
}

export interface PerkCreditAccount {
  account_id: string
  name: string
  mask: string | null
  institution_name: string | null
  card_id: number | null
  suggested_template_id: string | null
}

export interface PerksState {
  cards: PerkCard[]
  unmatched_credits: PerkUnmatchedCredit[]
  purchase_signals: PerkPurchaseSignal[]
  credit_accounts: PerkCreditAccount[]
}

export interface CatalogBenefit {
  id: string
  name: string
  detail: string
  amount_cents: number
  cadence: Cadence
  basis: ResetBasis
  kind: BenefitKind
  requires_enrollment: boolean
  month_overrides: Record<string, number>
}

export interface CardTemplate {
  id: string
  issuer: string
  name: string
  annual_fee_cents: number
  art: string
  benefits: CatalogBenefit[]
}

export interface Catalog {
  version: number
  reviewed: string
  templates: CardTemplate[]
}

// MARK: Dates

/** `yyyy-MM-dd` → local midnight. */
export function parseDay(value: string): Date {
  const [y, m, d] = value.slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d)
}

export function formatDay(date: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

/**
 * `anchor` plus `months`, clamping the day to the target month's length, like
 * Foundation's Calendar: Jan 31 + 1 month = Feb 28/29.
 */
export function addMonths(anchor: Date, months: number): Date {
  const total = anchor.getFullYear() * 12 + anchor.getMonth() + months
  const year = Math.floor(total / 12)
  const month = total - year * 12
  const lastDay = new Date(year, month + 1, 0).getDate()
  return new Date(year, month, Math.min(anchor.getDate(), lastDay))
}

export function daysBetween(from: Date, to: Date): number {
  // Round to absorb DST hour shifts.
  return Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / 86_400_000)
}

// MARK: Periods

/** Half-open `[start, end)`, both at local midnight. */
export interface Period {
  start: Date
  end: Date
}

export function lastDay(period: Period): Date {
  const d = new Date(period.end)
  d.setDate(d.getDate() - 1)
  return d
}

export function contains(period: Period, date: Date): boolean {
  return date >= period.start && date < period.end
}

export interface Schedule {
  cadence: Cadence
  basis: ResetBasis
  /** Account-open date; only read for `cardmember_year`. */
  anchor: Date
}

export function periodContaining(schedule: Schedule, date: Date): Period {
  const origin = schedule.basis === 'calendar' ? new Date(2000, 0, 1) : startOfDay(schedule.anchor)
  const length = CADENCE_MONTHS[schedule.cadence]
  const monthDelta = (date.getFullYear() - origin.getFullYear()) * 12 + (date.getMonth() - origin.getMonth())
  let index = Math.floor(monthDelta / length)
  // Always offset from the origin so a Jan 31 anchor yields Feb 29, then Mar 31.
  const at = (i: number): Period => ({
    start: addMonths(origin, i * length),
    end: addMonths(origin, (i + 1) * length),
  })
  // Day-of-month anchors can push the date into the neighbouring period.
  for (let attempt = 0; attempt < 4; attempt++) {
    const candidate = at(index)
    if (date < candidate.start) index -= 1
    else if (date >= candidate.end) index += 1
    else return candidate
  }
  return at(index)
}

export function nextAnniversary(opened: Date, onOrAfter: Date): Date {
  const anchor = startOfDay(opened)
  const day = startOfDay(onOrAfter)
  let years = Math.max(day.getFullYear() - anchor.getFullYear() - 1, 0)
  let candidate = addMonths(anchor, years * 12)
  while (candidate < day) {
    years += 1
    candidate = addMonths(anchor, years * 12)
  }
  // Opened today (or later): the next fee is a year out.
  if (candidate.getTime() === anchor.getTime()) candidate = addMonths(anchor, 12)
  return candidate
}

// MARK: Status

export interface BenefitStatus {
  period: Period
  totalCents: number
  usedCents: number
  remainingCents: number
  skipped: boolean
  resolved: boolean
  fractionUsed: number
}

export function amountFor(benefit: Pick<PerkBenefit, 'amount_cents' | 'cadence' | 'month_overrides'>, period: Period): number {
  if (benefit.cadence !== 'monthly') return benefit.amount_cents
  const override = benefit.month_overrides?.[String(period.start.getMonth() + 1)]
  return override ?? benefit.amount_cents
}

export function statusIn(benefit: PerkBenefit, period: Period): BenefitStatus {
  const inPeriod = benefit.usages.filter((u) => contains(period, parseDay(u.date)))
  const uses = inPeriod.filter((u) => !u.is_skip)
  const skipped = inPeriod.some((u) => u.is_skip)
  const totalCents = amountFor(benefit, period)
  const usedCents = uses.reduce((sum, u) => sum + u.amount_cents, 0)
  const isCertificate = benefit.kind === 'certificate'
  const remainingCents = isCertificate ? (uses.length > 0 ? 0 : totalCents) : Math.max(totalCents - usedCents, 0)
  const resolved = skipped || (isCertificate ? uses.length > 0 : remainingCents === 0)
  const fractionUsed = isCertificate
    ? (uses.length > 0 ? 1 : 0)
    : totalCents > 0 ? Math.min(usedCents / totalCents, 1) : 0
  return { period, totalCents, usedCents, remainingCents, skipped, resolved, fractionUsed }
}

export function scheduleFor(card: PerkCard, benefit: PerkBenefit): Schedule {
  return { cadence: benefit.cadence, basis: benefit.basis, anchor: parseDay(card.opened_on) }
}

export function benefitStatus(card: PerkCard, benefit: PerkBenefit, today: Date): BenefitStatus {
  return statusIn(benefit, periodContaining(scheduleFor(card, benefit), today))
}

/** Whole days left including today (1 on the last day). */
export function daysLeft(status: BenefitStatus, today: Date): number {
  return Math.max(daysBetween(today, status.period.end), 0)
}

// MARK: Items

/** Days after a matching purchase before a missing credit is worth flagging. */
export const MISSING_CREDIT_DAYS = 14

export interface PerkItem {
  card: PerkCard
  benefit: PerkBenefit
  status: BenefitStatus
  daysLeft: number
  /** A purchase at this benefit's merchant this period whose credit hasn't posted. */
  waitingOn: PerkPurchaseSignal | null
  creditLooksMissing: boolean
}

export function itemFor(state: PerksState, card: PerkCard, benefit: PerkBenefit, today: Date): PerkItem {
  const status = benefitStatus(card, benefit, today)
  const waitingOn = status.resolved
    ? null
    : state.purchase_signals
        .filter((s) => s.benefit_id === benefit.id && contains(status.period, parseDay(s.date)))
        .sort((a, b) => b.date.localeCompare(a.date))[0] ?? null
  return {
    card,
    benefit,
    status,
    daysLeft: daysLeft(status, today),
    waitingOn,
    creditLooksMissing: waitingOn !== null && daysBetween(parseDay(waitingOn.date), today) >= MISSING_CREDIT_DAYS,
  }
}

export function allItems(state: PerksState, today: Date): PerkItem[] {
  return state.cards.flatMap((card) => card.benefits.map((b) => itemFor(state, card, b, today)))
}

/** Unused benefits, soonest deadline first, bigger amounts first on ties. */
export function openItems(state: PerksState, today: Date): PerkItem[] {
  return allItems(state, today)
    .filter((i) => !i.status.resolved)
    .sort(
      (a, b) =>
        a.status.period.end.getTime() - b.status.period.end.getTime() ||
        b.status.remainingCents - a.status.remainingCents ||
        a.benefit.name.localeCompare(b.benefit.name)
    )
}

export function quarterOf(today: Date): Period {
  return periodContaining({ cadence: 'quarterly', basis: 'calendar', anchor: today }, today)
}

export function quarterName(period: Period): string {
  return `Q${Math.floor(period.start.getMonth() / 3) + 1}`
}

/** Dollar credits still unused that expire by the end of this quarter. */
export function unusedThisQuarter(state: PerksState, today: Date, cardId?: number): { cents: number; count: number } {
  const end = quarterOf(today).end
  const open = openItems(state, today).filter(
    (i) => i.benefit.kind === 'credit' && i.status.period.end <= end && (cardId === undefined || i.card.id === cardId)
  )
  return { cents: open.reduce((s, i) => s + i.status.remainingCents, 0), count: open.length }
}

export function cardYear(card: PerkCard, today: Date): Period {
  return periodContaining({ cadence: 'annual', basis: 'cardmember_year', anchor: parseDay(card.opened_on) }, today)
}

/** Value logged against a card during its current card-member year. */
export function capturedThisCardYear(card: PerkCard, today: Date): number {
  const year = cardYear(card, today)
  return card.benefits
    .flatMap((b) => b.usages)
    .filter((u) => !u.is_skip && contains(year, parseDay(u.date)))
    .reduce((s, u) => s + u.amount_cents, 0)
}

export function enrollmentNeeded(state: PerksState): Array<{ card: PerkCard; benefit: PerkBenefit }> {
  return state.cards.flatMap((card) =>
    card.benefits.filter((b) => b.requires_enrollment && !b.is_enrolled).map((benefit) => ({ card, benefit }))
  )
}

/** The unlinked card account that matches a template, if any. */
export function suggestedAccount(state: PerksState, templateId: string | null | undefined): PerkCreditAccount | null {
  if (!templateId) return null
  return state.credit_accounts.find((a) => a.card_id === null && a.suggested_template_id === templateId) ?? null
}

/** Cards without an account whose matching account is linked in Chequey. */
export function linkSuggestions(state: PerksState): Array<{ card: PerkCard; account: PerkCreditAccount }> {
  return state.cards.flatMap((card) => {
    const account = card.account_id === null ? suggestedAccount(state, card.template_id) : null
    return account ? [{ card, account }] : []
  })
}

export function annualValueCents(b: Pick<CatalogBenefit, 'amount_cents' | 'cadence' | 'month_overrides'>): number {
  if (b.cadence !== 'monthly') return Math.floor((b.amount_cents * 12) / CADENCE_MONTHS[b.cadence])
  let total = 0
  for (let m = 1; m <= 12; m++) total += b.month_overrides?.[String(m)] ?? b.amount_cents
  return total
}

/** Yearly face value of a template's dollar credits (certificates and 4-year credits excluded). */
export function templateCreditValueCents(t: CardTemplate): number {
  return t.benefits
    .filter((b) => b.kind === 'credit' && b.cadence !== 'every_four_years')
    .reduce((s, b) => s + annualValueCents(b), 0)
}

// MARK: Text

export function money(cents: number): string {
  const whole = cents % 100 === 0
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  })
}

export function shortDay(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export function periodName(cadence: Cadence, basis: ResetBasis): string {
  switch (cadence) {
    case 'monthly':
      return 'this month'
    case 'quarterly':
      return 'this quarter'
    case 'semiannual':
      return 'this half'
    case 'annual':
      return basis === 'cardmember_year' ? 'this card year' : 'this year'
    case 'every_four_years':
      return 'this 4-year cycle'
  }
}

export function cadenceSummary(b: Pick<PerkBenefit, 'amount_cents' | 'cadence' | 'kind'>): string {
  return b.kind === 'certificate' ? CADENCE_LABEL[b.cadence] : `${money(b.amount_cents)}${CADENCE_SHORT[b.cadence]}`
}

export function daysText(days: number): string {
  if (days <= 1) return 'Last day'
  if (days <= 60) return `${days} days`
  return `${Math.floor(days / 30)} mo`
}

export type Urgency = 'urgent' | 'soon' | 'calm'

export function urgency(days: number): Urgency {
  if (days <= 7) return 'urgent'
  if (days <= 21) return 'soon'
  return 'calm'
}

export function plural(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`
}

// MARK: Card art

/** Same faces as the iOS app. */
export const CARD_ART: Record<string, { from: string; to: string; light: boolean; label: string }> = {
  platinum: { from: '#DCDAD3', to: '#A8A59B', light: true, label: 'Platinum' },
  gold: { from: '#DDBA72', to: '#A67A2E', light: true, label: 'Gold' },
  sapphire: { from: '#26395F', to: '#0F1932', light: false, label: 'Sapphire' },
  'sapphire-light': { from: '#4B6795', to: '#2A416E', light: false, label: 'Blue' },
  obsidian: { from: '#2E2F2C', to: '#10110F', light: false, label: 'Black' },
  hilton: { from: '#18344F', to: '#0A1C2F', light: false, label: 'Navy' },
  'hilton-light': { from: '#7090AC', to: '#41607E', light: false, label: 'Slate' },
  marriott: { from: '#41403C', to: '#1D1C1A', light: false, label: 'Charcoal' },
  delta: { from: '#7D2533', to: '#44121B', light: false, label: 'Burgundy' },
  jade: { from: '#24704D', to: '#0F3B27', light: false, label: 'Green' },
  rose: { from: '#DCB0A2', to: '#A9725F', light: true, label: 'Rose' },
}

export function cardArt(key: string) {
  return CARD_ART[key] ?? CARD_ART.obsidian
}
